import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { DefaultError } from "@tanstack/query-core";
import identity from "lodash/identity";
import omit from "lodash/omit";

import {
  archiveScreener,
  copyScreener,
  getPreview,
  getReviewPageData,
  getScreenerBuilderDraft,
  getSlimScreenerBuilderDetails,
  saveCompetency,
  updateCompetency,
  createScreenerBuilder,
  updatePublishedScreener,
  updateScreenerBuilderStep,
  discardChanges,
  deleteCompetency,
  saveBasicQuestion,
  updateBasicQuestion,
  deleteBasicQuestion,
} from "@/app/api/screener-builder";
import {
  invalidateArchiveRestoreRelatedQueries,
  SCREENER_QUERY_KEYS,
} from "@/hooks/queries/screener.queries";
import { useAuthedQuery } from "@/hooks/queries/use-authed-query.hook";
import {
  AvailableLanguages,
  type PublishedScreenerBuilderUpdateBody,
  type ReviewPageBasicQuestion,
  type ReviewPageDraftQuestion,
  type SaveCompetencyPayload,
  type SaveCompetencyResponse,
  type ScreenerBuilderDataMap,
  type ScreenerBuilderResponse,
  type ScreenerBuilderReview,
  type ScreenerBuilderReviewPageResponse,
  ScreenerBuilderStep,
  type SlimScreenerBuilderDetails,
} from "@/types/screener-builder";
import type { DefaultUseQueryConfigParam } from "@/types/third-party";
import {
  EMPTY_BUILDER_CONTEXT,
  screenerIdIsNew,
} from "@/utils/screener-builder";
import { useEffect } from "react";

const SCREENER_BUILDER_QUERY_KEY_BASE = "screener_builder";
export const SCREENER_BUILDER_QUERY_KEYS = {
  base: [SCREENER_BUILDER_QUERY_KEY_BASE] as const,
  byScreenerId: (screenerId: string) => [
    ...SCREENER_BUILDER_QUERY_KEYS.base,
    screenerId,
  ],
  slimByScreenerId: (screenerId: string) => [
    ...SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
    "slim",
  ],
  screenerPreview: (screenerId: string) => [
    ...SCREENER_BUILDER_QUERY_KEYS.base,
    "preview",
    screenerId,
  ],
  reviewPageData: (screenerId: string) => [
    ...SCREENER_BUILDER_QUERY_KEYS.base,
    "review page data",
    screenerId,
  ],
};

export const useScreenerBuilderQuery = (
  screenerId: string,
  config: DefaultUseQueryConfigParam<ScreenerBuilderResponse> = {},
) => {
  return useAuthedQuery({
    // If `!!screenerId` and `!screenerIdIsNew(screenerId)` both evaluate to
    // truthy values, this will return the value of `config.enabled` even if it
    // is falsy (for example, the default value of `undefined`). This is the
    // intended behavior, but given && is often treated as a type coercion
    // operator I opted to leave this comment for additional clarity.
    enabled: !!screenerId && !screenerIdIsNew(screenerId) && config.enabled,
    queryFn: () => getScreenerBuilderDraft(screenerId),
    queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
    ...config,
  });
};

export const useCreateScreenerBuilderMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (body: ScreenerBuilderDataMap[ScreenerBuilderStep.Position]) =>
      createScreenerBuilder(body),
    onSuccess: ({ currentPage: step, ...response }, body) => {
      // Invalidate the screener table query to ensure the new draft is included
      // in the table when the user returns.
      client.invalidateQueries({ queryKey: SCREENER_QUERY_KEYS.base });

      client.setQueryData(
        SCREENER_BUILDER_QUERY_KEYS.byScreenerId(response.screenerId),
        {
          ...response,
          data: {
            context: EMPTY_BUILDER_CONTEXT,
            position: omit(body, "progressStep"),
          },
          isLegacyScreener: false,
          step,
        },
      );
    },
  });
};

export const useUpdateScreenerBuilderStepMutation = <
  T extends ScreenerBuilderStep,
>() => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({
      body,
      screenerId,
      step,
    }: {
      body: ScreenerBuilderDataMap[T];
      screenerId: string;
      step: T;
    }) => updateScreenerBuilderStep<T>(screenerId, step, body),
    onSuccess: (updatedBuilderInfo, { screenerId, step }) => {
      if (step !== ScreenerBuilderStep.Review) {
        client.setQueryData(
          SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
          updatedBuilderInfo,
        );
      }
      return client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.slimByScreenerId(screenerId),
      });
    },
  });
};

const getSlimScreenerDetailsBaseQueryConfig = (screenerId: string) => ({
  queryKey: SCREENER_BUILDER_QUERY_KEYS.slimByScreenerId(screenerId),
  queryFn: () => getSlimScreenerBuilderDetails(screenerId),
});

export const useSlimScreenerDetailsQuery = (
  screenerId: string,
  config: DefaultUseQueryConfigParam<SlimScreenerBuilderDetails> = {},
) => {
  return useAuthedQuery({
    ...getSlimScreenerDetailsBaseQueryConfig(screenerId),
    ...config,
  });
};

export const usePrefetchSlimScreenerDetailsQuery = (screenerId?: string) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (screenerId) {
      queryClient.prefetchQuery(
        getSlimScreenerDetailsBaseQueryConfig(screenerId),
      );
    }
  }, [queryClient, screenerId]);
};

export const useScreenerPreviewQuery = (
  screenerId: string,
  config: DefaultUseQueryConfigParam<string> = {},
) => {
  return useAuthedQuery({
    queryKey: SCREENER_BUILDER_QUERY_KEYS.screenerPreview(screenerId),
    queryFn: () => getPreview(screenerId),
    ...config,
  });
};

export const useCopyScreenerMutation = () => {
  const client = useQueryClient();

  return useMutation<string, DefaultError, string>({
    mutationFn: copyScreener,
    onSuccess: () => {
      // Ensure the copied screener is fetched the next time a user views the
      // screeners table
      client.invalidateQueries({ queryKey: SCREENER_QUERY_KEYS.base });
    },
  });
};

export const useArchiveScreenerMutation = () => {
  const client = useQueryClient();

  return useMutation<string, DefaultError, string>({
    mutationFn: archiveScreener,
    onSuccess: async (response, deletedId) =>
      await invalidateArchiveRestoreRelatedQueries(client, deletedId),
  });
};

export const useUpdateScreenerMessagesMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation<
    ScreenerBuilderResponse,
    DefaultError,
    PublishedScreenerBuilderUpdateBody
  >({
    mutationFn: (messages) => updatePublishedScreener(screenerId, messages),
    onSuccess: () => {
      // Invalidate both the screener builder and preview queries
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
      });
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.screenerPreview(screenerId),
      });
      // Also invalidate base screener queries to update any listings
      client.invalidateQueries({
        queryKey: SCREENER_QUERY_KEYS.base,
      });
    },
    onError: (error) => {
      console.error("Error updating screener messages:", error);
    },
  });
};

export const useSaveCompetencyMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation<
    SaveCompetencyResponse,
    DefaultError,
    SaveCompetencyPayload
  >({
    mutationFn: saveCompetency,
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
      });
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.reviewPageData(screenerId),
      });
    },
  });
};

export const useUpdateCompetencyMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation<
    SaveCompetencyResponse,
    DefaultError,
    { competencyId: string; payload: SaveCompetencyPayload }
  >({
    mutationFn: ({ competencyId, payload }) =>
      updateCompetency(competencyId, payload),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
      });
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.reviewPageData(screenerId),
      });
    },
  });
};

export const useDeleteCompetencyMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation<void, DefaultError, { competencyId: string }>({
    mutationFn: ({ competencyId }) => deleteCompetency(competencyId),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
      });
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.reviewPageData(screenerId),
      });
    },
  });
};

export const useSaveBasicQuestionMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation<
    ReviewPageBasicQuestion,
    DefaultError,
    { payload: ReviewPageDraftQuestion }
  >({
    mutationFn: ({ payload }) => saveBasicQuestion(payload, screenerId),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
      });
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.reviewPageData(screenerId),
      });
    },
  });
};

export const useUpdateBasicQuestionMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation<
    ReviewPageBasicQuestion,
    DefaultError,
    { payload: ReviewPageBasicQuestion }
  >({
    mutationFn: ({ payload }) => updateBasicQuestion(payload, screenerId),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
      });
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.reviewPageData(screenerId),
      });
    },
  });
};

export const useDeleteBasicQuestionMutation = (screenerId: string) => {
  const client = useQueryClient();

  return useMutation<void, DefaultError, { questionId: string }>({
    mutationFn: ({ questionId }) => deleteBasicQuestion(questionId),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
      });
      client.invalidateQueries({
        queryKey: SCREENER_BUILDER_QUERY_KEYS.reviewPageData(screenerId),
      });
    },
  });
};

// This query should typically not be used outside of the context of the
// useReviewPageData hook. There are differences in how competencies are
// formatted between CSV and AI builder screeners that are not apparent when
// this query's data is viewed in isolation.
export const useReviewPageQuery = <TSelectData = ScreenerBuilderReview>(
  screenerId: string,
  // Replace config's `select` with a new parameter to allow transforming the
  // response to the ScreenerBuilderReview type inside the query before handing
  // it off to consumers.
  config: Omit<
    DefaultUseQueryConfigParam<
      ScreenerBuilderReviewPageResponse,
      DefaultError,
      TSelectData
    >,
    "select"
  > = {},
  select: (data: ScreenerBuilderReview) => TSelectData = identity,
) => {
  return useAuthedQuery({
    enabled: !!screenerId && !screenerIdIsNew(screenerId),
    queryFn: () => getReviewPageData(screenerId),
    queryKey: SCREENER_BUILDER_QUERY_KEYS.reviewPageData(screenerId),
    select: ({ languages, ...rest }) =>
      select({
        languages:
          languages && languages.length > 0
            ? languages
            : [AvailableLanguages.English],
        ...rest,
      }),
    ...config,
  });
};

export const useDiscardChangesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, DefaultError, { screenerId: string }>({
    mutationFn: ({ screenerId }) => discardChanges(screenerId),
    onSuccess: (_, { screenerId }) =>
      Promise.allSettled([
        queryClient.invalidateQueries({
          queryKey: SCREENER_BUILDER_QUERY_KEYS.slimByScreenerId(screenerId),
        }),
        queryClient.invalidateQueries({
          queryKey: SCREENER_BUILDER_QUERY_KEYS.reviewPageData(screenerId),
        }),
        queryClient.invalidateQueries({
          queryKey: SCREENER_BUILDER_QUERY_KEYS.byScreenerId(screenerId),
        }),
        queryClient.invalidateQueries({
          queryKey: SCREENER_BUILDER_QUERY_KEYS.screenerPreview(screenerId),
        }),
      ]),
  });
};
